import React from 'react';
import { JsonTableData } from '../features/Model';
import { RaceData } from '../features/RaceDataUtil';
import { MenuViewData } from '../features/ViewModel';
import { defined, Optional, setDefault } from '../utils/Util';
import { useApplicationContext } from './Context';

const useJsonPath = () => {
  const basePath = useApplicationContext().jsonBasePath;
  const jsonDirPath = `${basePath}data/`;
  return {
    paths: jsonDirPath + 'paths.json',
    redirects: jsonDirPath + 'redirects.json',
    race: basePath + 'setting/race/race.json',
    getPageDataPath: (path: string) => jsonDirPath + path + '/tables.json',
    getTablePath: (path: string) => jsonDirPath + path,
  };
};

const useLayoutPath = () => {
  const basePath = useApplicationContext().layoutBasePath;
  return {
    header: basePath + 'header.json',
    pagination: basePath + 'pagination.json',
  };
};

export type PathData = { [key: string]: PathData | string };

export type HeaderData = {
  buttons: Record<string, MenuViewData>;
};

export type PaginationData = {
  regexHidePagination: string;
};

export interface FileState<T> {
  isLoading: boolean;
  data: T;
  error?: Error;
}

const dataMap: Map<string, unknown> = new Map();

/* 以下は state を使用してローディング状態を管理 */
const useBaseAsync = <T, V>(
  path: string,
  initialValue: T,
  map: (data: V) => T
): FileState<T> => {
  const cachedData = dataMap.get(path) as Optional<T>;

  const initialState = defined(cachedData)
    ? { data: cachedData, isLoading: false }
    : {
        data: initialValue,
        isLoading: true,
      };
  const [state, setState] = React.useState(initialState as FileState<T>);
  React.useEffect(() => {
    if (defined(cachedData)) {
      // キャッシュを使用する場合はfetchを呼ばない
      if (state.data !== cachedData) {
        // 2回目以降の処理ではinitialStateに代入されないため、setする
        setState({ data: cachedData, isLoading: false });
      }
      return;
    }
    // state.isLoadingは2回目以降の実行だと、この時点でfalseの可能性がある
    // そのためtrueに変更する
    setState((prev) => ({ ...prev, isLoading: true }));
    // isLoadingはthenの中で即時反映されない可能性があるので条件分岐には使用しない
    let mounted = true;
    fetch(path)
      .then((res) => res.json())
      .then(
        (result: V) => {
          if (mounted) {
            const data = map(result);
            setState({ data, isLoading: false });
            dataMap.set(path, data);
          }
        },
        (error: Error) => {
          if (mounted) {
            setState({ data: initialValue, error, isLoading: false });
          }
        }
      );
    return () => {
      mounted = false;
      setState((prev) => ({ ...prev, isLoading: false }));
    };
  }, [path]);
  return state;
};

/* 以下は Suspense を使用してローディング状態を管理 */
const fetchJsonData = <T, V = T>(
  path: string,
  map: (data: V) => T = (data) => data as unknown as T
): T => {
  const cachedData = dataMap.get(path) as Optional<T>;
  if (!defined(cachedData)) {
    throw fetch(path)
      .then((res) => res.json())
      .then((data) => dataMap.set(path, map(data)));
  } else {
    return cachedData;
  }
};

const appendSlashToStrings = (obj: unknown): unknown => {
  // すべての文字列要素の先頭に'/'を足す
  if (typeof obj === 'string') {
    return obj.startsWith('/') ? obj : '/' + obj;
  } else if (Array.isArray(obj)) {
    return obj.map(appendSlashToStrings);
  } else if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = appendSlashToStrings(value);
    }
    return result;
  } else {
    return obj;
  }
};

const fetchRaceData = (path: string): RaceData =>
  fetchJsonData<RaceData, JsonTableData>(path, (jsonData) => {
    const raceData: RaceData = {};
    // スプリント対応
    const raceCount: Record<string, number[]> = {};

    for (const { data } of jsonData.rows) {
      const [raceId, year, round] = data.map((cell) =>
        parseInt(cell[0]?.[0] ?? '')
      );
      if (defined(raceId) && defined(year) && defined(round)) {
        raceData[raceId] = { year, round };
        const roundId = `${year}-${round}`;
        setDefault(raceCount, roundId, []).push(raceId);
      }
    }
    // スプリント対応
    for (const raceIds of Object.values(raceCount)) {
      if (raceIds.length > 1) {
        for (const [index, raceId] of raceIds.entries()) {
          const race = raceData[raceId];
          if (defined(race)) {
            raceData[raceId] = { ...race, index };
          }
        }
      }
    }
    return raceData;
  });

export const useTableData = <T, V>(
  path: string,
  initialValue: T,
  map: (data: V) => T
): FileState<T> => {
  const jsonPath = useJsonPath();
  return useBaseAsync(jsonPath.getTablePath(path), initialValue, map);
};

export const useData: () => {
  fetchPathData: () => PathData;
  fetchPageData: (path: string) => string[];
  fetchRedirectData: () => { [key: string]: string };
  fetchRaceData: () => RaceData;
  fetchHeaderData: () => HeaderData;
  fetchPaginationData: () => PaginationData;
  fetchTheOtherSitePathData: () => PathData;
} = () => {
  const jsonPath = useJsonPath();
  const layoutPath = useLayoutPath();

  const {
    theOtherSiteJsonBasePath,
    headerProps: { linkPath },
  } = useApplicationContext();
  const theOtherSitePathsPath = `${linkPath}${theOtherSiteJsonBasePath}data/paths.json`;

  return {
    fetchPathData: () =>
      fetchJsonData<PathData>(
        jsonPath.paths,
        (obj) => appendSlashToStrings(obj) as PathData
      ),

    fetchPageData: (path: string): string[] =>
      fetchJsonData(jsonPath.getPageDataPath(path)),

    fetchRedirectData: () => fetchJsonData(jsonPath.redirects),

    fetchRaceData: () => fetchRaceData(jsonPath.race),
    fetchHeaderData: () => fetchJsonData(layoutPath.header),
    fetchPaginationData: () => fetchJsonData(layoutPath.pagination),
    fetchTheOtherSitePathData: () => fetchJsonData(theOtherSitePathsPath),
  };
};
